import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronLeft, Trash2, Plus } from 'lucide-react';
import { OCRReviewForm, CATEGORIAS, FORMAS_PAGAMENTO } from './OCRReviewForm';
import type { ReviewFormValues } from './OCRReviewForm';
import type { OCRResult, DocType } from './types';

const DOC_LABELS: Record<DocType, string> = {
  nfe: 'Nota fiscal',
  recibo: 'Recibo',
  boleto: 'Boleto',
  extrato: 'Extrato bancário',
};

const reviewSchema = z.object({
  emitenteNome: z.string().min(1, 'Informe o fornecedor'),
  emitenteCnpj: z.string().min(1, 'Informe o CNPJ'),
  valorTotal: z
    .string()
    .min(1, 'Informe o valor')
    .refine((v) => !isNaN(parseFloat(v.replace(',', '.'))), 'Valor inválido'),
  dataEmissao: z.string().min(1, 'Informe a data'),
  categoria: z.string().min(1, 'Informe a categoria'),
  formaPagamento: z.string().min(1, 'Informe a forma de pagamento'),
});

interface OCRReviewProps {
  ocr: OCRResult;
  docType: DocType;
  shot: string | null;
  isSaving?: boolean;
  onConfirm: (values: ReviewFormValues) => void;
  onDiscard: () => void;
  onBack: () => void;
}

/**
 * Tela de revisão dos campos extraídos pelo OCR.
 * Campos editáveis + footer com Descartar / Adicionar lançamento.
 * Formulário delegado a OCRReviewForm (separação de responsabilidade).
 */
export function OCRReview({ ocr, docType, shot, isSaving = false, onConfirm, onDiscard, onBack }: OCRReviewProps) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      emitenteNome: ocr.emitenteNome,
      emitenteCnpj: ocr.emitenteCnpj,
      valorTotal: ocr.valorTotal.toFixed(2).replace('.', ','),
      dataEmissao: ocr.dataEmissao,
      categoria: ocr.categoria,
      formaPagamento: ocr.formaPagamento,
    },
  });

  useEffect(() => {
    reset({
      emitenteNome: ocr.emitenteNome,
      emitenteCnpj: ocr.emitenteCnpj,
      valorTotal: ocr.valorTotal.toFixed(2).replace('.', ','),
      dataEmissao: ocr.dataEmissao,
      categoria: CATEGORIAS.includes(ocr.categoria) ? ocr.categoria : CATEGORIAS[0],
      formaPagamento: FORMAS_PAGAMENTO.includes(ocr.formaPagamento) ? ocr.formaPagamento : FORMAS_PAGAMENTO[0],
    });
  }, [ocr, reset]);

  const pct = Math.round(ocr.confianca * 100);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--frame)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 18px 12px',
          borderBottom: '1px solid var(--card-stroke)',
          flexShrink: 0,
        }}
      >
        <button onClick={onBack} aria-label="Voltar para câmera" style={headerIconBtn}>
          <ChevronLeft size={18} strokeWidth={2} aria-hidden />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em' }}>Documento lido</div>
          <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 1 }}>Confira os dados extraídos pela IA</div>
        </div>
        {/* AI Badge */}
        <span
          aria-label={`OCR com ${pct}% de confiança`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 9px', borderRadius: 'var(--r-pill)',
            background: 'var(--lilac-soft, #EDE8F9)',
            color: 'var(--lilac-deep, #7C5CBF)',
            fontSize: 11, fontWeight: 700,
            fontFamily: 'var(--font-mono)', letterSpacing: '0.02em', whiteSpace: 'nowrap',
          }}
        >
          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--lilac-deep, #7C5CBF)', display: 'inline-block' }} />
          OCR · {pct}%
        </span>
      </div>

      {/* Corpo scrollável */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Thumbnail */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 60, height: 80, borderRadius: 'var(--r-2)', overflow: 'hidden', border: '1px solid var(--card-stroke)', background: 'var(--card-soft)', flexShrink: 0 }}>
            {shot
              ? <img src={shot} alt="Foto capturada" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <div aria-hidden="true" style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'var(--fg-dim)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
            }
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', letterSpacing: '-0.01em' }}>{DOC_LABELS[docType]}</div>
            <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>1 página · extraído por IA</div>
          </div>
        </div>

        <OCRReviewForm register={register} errors={errors} />
      </div>

      {/* Footer fixo */}
      <div
        style={{
          display: 'flex', gap: 10,
          padding: '12px 18px',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)',
          borderTop: '1px solid var(--card-stroke)',
          background: 'var(--frame)', flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onDiscard}
          aria-label="Descartar documento escaneado"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0 16px', height: 46, borderRadius: 'var(--r-3)',
            background: 'var(--card-soft)', color: 'var(--block)',
            fontSize: 14, fontWeight: 600,
            border: '1px solid var(--card-stroke)', cursor: 'pointer', flexShrink: 0,
          }}
        >
          <Trash2 size={15} strokeWidth={1.8} aria-hidden />
          Descartar
        </button>
        <button
          type="submit"
          form="ocr-review-form"
          onClick={handleSubmit(onConfirm)}
          disabled={isSaving}
          aria-label={isSaving ? 'Salvando lançamento' : 'Confirmar e adicionar lançamento'}
          aria-busy={isSaving}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 7, height: 46, borderRadius: 'var(--r-3)',
            background: isSaving ? 'var(--mint)' : 'var(--mint-deep)',
            color: 'var(--on-ink)',
            fontSize: 14, fontWeight: 700, border: 'none',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.8 : 1,
            transition: 'background 0.15s, opacity 0.15s',
          }}
        >
          {isSaving ? (
            <>
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block', width: 14, height: 14,
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.35)',
                  borderTopColor: '#fff',
                  animation: 'spin 0.7s linear infinite',
                }}
              />
              Salvando…
            </>
          ) : (
            <>
              <Plus size={16} strokeWidth={2} aria-hidden />
              Adicionar lançamento
            </>
          )}
        </button>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const headerIconBtn: React.CSSProperties = {
  width: 36, height: 36, borderRadius: '50%',
  background: 'var(--card-soft)', border: '1px solid var(--card-stroke)',
  color: 'var(--fg)', display: 'grid', placeItems: 'center',
  cursor: 'pointer', flexShrink: 0,
};
